import React, { useRef, useState } from "react";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Upload, Download, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

async function downloadFile(url, filename) {
  const token = localStorage.getItem("aceh_token") || "";
  const resp = await fetch(url, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
  const blob = await resp.blob();
  const dl = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = dl; a.download = filename; a.click(); URL.revokeObjectURL(dl);
}

export default function ImportProvidersDialog({ open, onOpenChange, onImported }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setResult(null);
    const form = new FormData();
    form.append("file", file);
    const token = localStorage.getItem("aceh_token") || "";
    try {
      const resp = await fetch(`${API}/providers-bulk/import`, {
        method: "POST", body: form, credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.detail || "Import failed");
      setResult(data);
      toast.success(`Imported: ${data.created} new · ${data.updated} updated`);
      onImported?.();
    } catch (e) { toast.error(e.message || "Import failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) { setResult(null); } }}>
      <DialogContent className="max-w-lg" data-testid="prov-import-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Upload className="w-5 h-5" /> Import providers from Excel</DialogTitle></DialogHeader>
        <div className="text-sm text-slate-600 space-y-2">
          <p>Upload an <code className="font-mono-plex text-xs bg-slate-100 rounded px-1">.xlsx</code> or <code className="font-mono-plex text-xs bg-slate-100 rounded px-1">.csv</code> file. Existing rows with the same <b>name + specialty</b> are updated; new rows are created. Optional <code className="font-mono-plex text-xs bg-slate-100 rounded px-1">employment_type</code> column accepts FTE, PT or IC.</p>
          <p>Valid specialties: <span className="font-mono-plex text-xs">Psychiatrist, PMHNP, PsychD, LCSW, APRN</span>.</p>
        </div>
        <div className="flex flex-col gap-2">
          <Button variant="outline" onClick={() => downloadFile(`${API}/providers-bulk/template.xlsx`, "providers-template.xlsx")} data-testid="prov-import-template">
            <Download className="w-4 h-4 mr-1" /> Download Excel template
          </Button>
          <input
            ref={fileRef} type="file" hidden accept=".xlsx,.xls,.csv"
            onChange={(e) => upload(e.target.files?.[0])}
            data-testid="prov-import-file"
          />
          <Button onClick={() => fileRef.current?.click()} disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="prov-import-choose">
            <Upload className="w-4 h-4 mr-1" /> {busy ? "Uploading…" : "Choose file to upload"}
          </Button>
        </div>

        {result && (
          <Card className="p-4 border-slate-200 shadow-none mt-2" data-testid="prov-import-result">
            <div className="flex items-center gap-2 text-sm text-emerald-700">
              <CheckCircle2 className="w-4 h-4" /> {result.created} created · {result.updated} updated · {result.total_rows} rows read
            </div>
            {result.errors?.length > 0 && (
              <div className="mt-3">
                <div className="text-xs uppercase tracking-widest font-mono-plex text-rose-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Skipped ({result.errors.length})</div>
                <div className="mt-1 max-h-40 overflow-y-auto text-xs space-y-1">
                  {result.errors.map((e, i) => <div key={i} className="text-slate-600">Row {e.row}: {e.reason}</div>)}
                </div>
              </div>
            )}
          </Card>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
